import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, FileText, Check, AlertCircle, Loader } from 'lucide-react';

const ResumeUploadStep = ({ 
  formData, 
  updateFormData, 
  onNext, 
  onPrev, 
  onSkip 
}) => {
  const [file, setFile] = useState(formData.resume.file || null);
  const [fileName, setFileName] = useState(formData.resume.file?.name || '');
  const [fileSize, setFileSize] = useState(formData.resume.file?.size || 0);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [parseSuccess, setParseSuccess] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndProcessFile(selectedFile);
    }
  };

  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndProcessFile(e.dataTransfer.files[0]);
    }
  };

  // Validate and process the file
  const validateAndProcessFile = (selectedFile) => {
    // Reset states
    setError('');
    setParseSuccess(false);
    
    // Check file type
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(selectedFile.type)) {
      setError('Please upload a PDF or Word document');
      return;
    }
    
    // Check file size (max 5MB)
    if (selectedFile.size > 5 * 1024 * 1024) {
      setError('File size should be less than 5MB');
      return;
    }
    
    // Set file info
    setFile(selectedFile);
    setFileName(selectedFile.name);
    setFileSize(selectedFile.size);
    
    // Simulate upload
    simulateUpload(selectedFile);
  };

  // Simulate file upload with progress
  const simulateUpload = (selectedFile) => {
    setIsUploading(true);
    setUploadProgress(0);
    
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsUploading(false);
          simulateParsing(selectedFile);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  // Simulate resume parsing
  const simulateParsing = (selectedFile) => {
    setIsParsing(true);
    
    // In a real app, this would call an API to parse the resume
    // We're simulating a more realistic parsing process with different timing based on file size
    const parsingTime = Math.min(2000 + (selectedFile.size / 1024 / 10), 4000); // Between 2-4 seconds based on file size
    
    setTimeout(() => {
      setIsParsing(false);
      setParseSuccess(true);
      
      // Generate more realistic parsed data based on file name to simulate different resumes
      // This creates a deterministic but varied result based on the file name
      const fileNameHash = selectedFile.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const randomSeed = fileNameHash % 1000;
      
      // Mock parsed data with more comprehensive information
      const mockParsedData = generateMockResumeData(randomSeed);
      
      // Update form data with file and parsed information
      updateFormData('resume', {
        file: selectedFile,
        parsed: true,
        parsedData: mockParsedData
      });
      
      // Also update the skills, experience, and education sections
      updateFormData('skills', mockParsedData.skills);
      updateFormData('experience', mockParsedData.experience);
      updateFormData('education', mockParsedData.education);
      
      // Update preferences based on parsed resume
      updateFormData('preferences', {
        jobTypes: mockParsedData.preferences.jobTypes,
        industries: mockParsedData.preferences.industries,
        locations: mockParsedData.preferences.locations,
        jobTitle: mockParsedData.preferences.jobTitle,
        workExperience: mockParsedData.preferences.workExperience
      });
      
      // Update basic info if available
      if (mockParsedData.basicInfo) {
        updateFormData('basicInfo', {
          ...formData.basicInfo,
          ...mockParsedData.basicInfo
        });
      }
      
    }, parsingTime);
  };
  
  // Generate mock resume data based on a seed for deterministic but varied results
  const generateMockResumeData = (seed) => {
    // List of possible skills by category
    const techSkills = ['JavaScript', 'React', 'Angular', 'Vue.js', 'Node.js', 'Express', 'MongoDB', 'SQL', 'Python', 'Django', 'Flask', 'Java', 'Spring Boot', 'C#', '.NET', 'PHP', 'Laravel', 'HTML', 'CSS', 'SASS', 'LESS', 'TypeScript', 'GraphQL', 'REST API', 'AWS', 'Azure', 'GCP', 'Docker', 'Kubernetes', 'CI/CD', 'Git', 'GitHub', 'GitLab', 'Jira', 'Agile', 'Scrum', 'TDD', 'Redux', 'MobX', 'Webpack', 'Babel', 'ESLint', 'Jest', 'Mocha', 'Chai', 'Cypress', 'Selenium'];
    const designSkills = ['UI/UX Design', 'Figma', 'Adobe XD', 'Sketch', 'Photoshop', 'Illustrator', 'InDesign', 'Wireframing', 'Prototyping', 'User Research', 'Usability Testing', 'Information Architecture', 'Visual Design', 'Interaction Design', 'Responsive Design', 'Mobile Design', 'Web Design', 'Branding', 'Typography', 'Color Theory'];
    const marketingSkills = ['Digital Marketing', 'SEO', 'SEM', 'Google Analytics', 'Google Ads', 'Facebook Ads', 'Instagram Marketing', 'Content Marketing', 'Email Marketing', 'Social Media Marketing', 'Influencer Marketing', 'Brand Management', 'Market Research', 'Copywriting', 'A/B Testing', 'Conversion Rate Optimization', 'Customer Acquisition', 'Marketing Automation', 'Marketing Strategy', 'Campaign Management'];
    const dataSkills = ['Data Analysis', 'Data Visualization', 'SQL', 'Python', 'R', 'Tableau', 'Power BI', 'Excel', 'Machine Learning', 'Deep Learning', 'NLP', 'Computer Vision', 'Statistical Analysis', 'Big Data', 'Hadoop', 'Spark', 'ETL', 'Data Modeling', 'Data Mining', 'Predictive Analytics'];
    
    // Companies by industry
    const techCompanies = ['Google', 'Microsoft', 'Amazon', 'Apple', 'Meta', 'IBM', 'Oracle', 'Intel', 'Cisco', 'Infosys', 'TCS', 'Wipro', 'HCL', 'Tech Mahindra', 'Cognizant', 'Accenture', 'Capgemini', 'Deloitte Digital', 'Mindtree', 'Persistent Systems'];
    const financeCompanies = ['HDFC Bank', 'ICICI Bank', 'SBI', 'Axis Bank', 'Kotak Mahindra Bank', 'Yes Bank', 'RBI', 'LIC', 'Bajaj Finance', 'IDFC First Bank', 'Federal Bank', 'IndusInd Bank', 'PayTM', 'PhonePe', 'Razorpay', 'PolicyBazaar', 'Zerodha', 'Groww', 'CRED', 'Upstox'];
    const ecommerceCompanies = ['Flipkart', 'Amazon India', 'Myntra', 'Snapdeal', 'Nykaa', 'BigBasket', 'Grofers', 'Swiggy', 'Zomato', 'MakeMyTrip', 'Goibibo', 'OYO', 'Ola', 'Uber', 'Urban Company', 'Meesho', 'Ajio', 'Tata CLiQ', 'Reliance Digital', 'Pepperfry'];
    
    // Universities
    const universities = ['IIT Bombay', 'IIT Delhi', 'IIT Madras', 'IIT Kanpur', 'IIT Kharagpur', 'IIT Roorkee', 'IIT Guwahati', 'IIT Hyderabad', 'BITS Pilani', 'Delhi University', 'Mumbai University', 'Pune University', 'Anna University', 'VIT', 'Manipal Institute of Technology', 'NIT Trichy', 'NIT Warangal', 'NIT Surathkal', 'IIIT Hyderabad', 'ISB Hyderabad'];
    
    // Job titles by field
    const techTitles = ['Software Engineer', 'Frontend Developer', 'Backend Developer', 'Full Stack Developer', 'DevOps Engineer', 'QA Engineer', 'Data Engineer', 'Mobile Developer', 'UI Developer', 'System Architect', 'Technical Lead', 'Engineering Manager', 'CTO', 'VP of Engineering', 'Product Manager', 'Scrum Master', 'IT Manager', 'Network Administrator', 'Security Engineer', 'Cloud Engineer'];
    const designTitles = ['UI Designer', 'UX Designer', 'UI/UX Designer', 'Product Designer', 'Graphic Designer', 'Visual Designer', 'Interaction Designer', 'Web Designer', 'Mobile Designer', 'Art Director', 'Creative Director', 'Brand Designer', 'Design Manager', 'Design Lead', 'Design Strategist', 'UX Researcher', 'Information Architect', 'Design Systems Engineer', 'Motion Designer', 'Illustrator'];
    const marketingTitles = ['Marketing Manager', 'Digital Marketing Specialist', 'SEO Specialist', 'Content Marketer', 'Social Media Manager', 'Growth Hacker', 'Brand Manager', 'Marketing Analyst', 'Marketing Coordinator', 'Campaign Manager', 'Email Marketing Specialist', 'Marketing Director', 'CMO', 'PR Manager', 'Communications Manager', 'Marketing Strategist', 'Product Marketing Manager', 'Demand Generation Manager', 'Marketing Operations Manager', 'Community Manager'];
    
    // Use the seed to deterministically select data
    const getSeededRandom = (max, offset = 0) => {
      return (seed + offset) % max;
    };
    
    const getSeededRandomArray = (array, count, offset = 0) => {
      const result = [];
      const indices = new Set();
      
      for (let i = 0; i < count; i++) {
        let index = (seed + offset + i * 17) % array.length;
        while (indices.has(index)) {
          index = (index + 1) % array.length;
        }
        indices.add(index);
        result.push(array[index]);
      }
      
      return result;
    };
    
    // Determine the profile type based on seed
    const profileTypes = ['tech', 'design', 'marketing', 'data'];
    const profileType = profileTypes[getSeededRandom(profileTypes.length)];
    
    // Select skills based on profile type
    let skills = [];
    let jobTitles = [];
    let companies = [];
    
    switch (profileType) {
      case 'tech':
        skills = getSeededRandomArray(techSkills, 5 + getSeededRandom(6));
        jobTitles = techTitles;
        companies = techCompanies;
        break;
      case 'design':
        skills = getSeededRandomArray(designSkills, 4 + getSeededRandom(5));
        jobTitles = designTitles;
        companies = [...techCompanies, ...ecommerceCompanies];
        break;
      case 'marketing':
        skills = getSeededRandomArray(marketingSkills, 4 + getSeededRandom(5));
        jobTitles = marketingTitles;
        companies = [...ecommerceCompanies, ...financeCompanies];
        break;
      case 'data':
        skills = getSeededRandomArray(dataSkills, 4 + getSeededRandom(5));
        jobTitles = ['Data Analyst', 'Data Scientist', 'Business Analyst', 'Data Engineer', 'ML Engineer'];
        companies = [...techCompanies, ...financeCompanies];
        break;
    }
    
    // Generate experience entries
    const experienceCount = 1 + getSeededRandom(3); // 1-3 experiences
    const experiences = [];
    
    let currentYear = new Date().getFullYear();
    let currentMonth = new Date().getMonth() + 1;
    
    for (let i = 0; i < experienceCount; i++) {
      const durationMonths = 6 + getSeededRandom(30, i * 100); // 6-36 months
      const endYear = i === 0 ? currentYear : currentYear - Math.floor(i * 1.5);
      const endMonth = i === 0 ? currentMonth : 1 + getSeededRandom(12, i * 50);
      
      const startYear = endYear - Math.floor(durationMonths / 12);
      const startMonth = endMonth - (durationMonths % 12);
      
      const adjustedStartYear = startMonth <= 0 ? startYear - 1 : startYear;
      const adjustedStartMonth = startMonth <= 0 ? 12 + startMonth : startMonth;
      
      const formattedStartMonth = adjustedStartMonth.toString().padStart(2, '0');
      const formattedEndMonth = endMonth.toString().padStart(2, '0');
      
      experiences.push({
        title: jobTitles[getSeededRandom(jobTitles.length, i * 10)],
        company: companies[getSeededRandom(companies.length, i * 20)],
        location: ['Mumbai', 'Bangalore', 'Hyderabad', 'Delhi', 'Pune', 'Chennai'][getSeededRandom(6, i * 30)],
        startDate: `${adjustedStartYear}-${formattedStartMonth}`,
        endDate: i === 0 && getSeededRandom(2) === 0 ? '' : `${endYear}-${formattedEndMonth}`,
        current: i === 0 && getSeededRandom(2) === 0,
        description: generateJobDescription(profileType, getSeededRandom(1000, i * 40))
      });
      
      currentYear = adjustedStartYear;
      currentMonth = adjustedStartMonth;
    }
    
    // Sort experiences by date (most recent first)
    experiences.sort((a, b) => {
      const aEnd = a.current ? '9999-12' : a.endDate;
      const bEnd = b.current ? '9999-12' : b.endDate;
      return bEnd.localeCompare(aEnd);
    });
    
    // Generate education entries
    const educationCount = 1 + getSeededRandom(2); // 1-2 education entries
    const education = [];
    
    for (let i = 0; i < educationCount; i++) {
      const university = universities[getSeededRandom(universities.length, i * 60)];
      const isUndergrad = i === educationCount - 1;
      
      const degree = isUndergrad 
        ? ['Bachelor of Technology', 'Bachelor of Engineering', 'Bachelor of Science', 'Bachelor of Commerce', 'Bachelor of Arts'][getSeededRandom(5, i * 70)]
        : ['Master of Technology', 'Master of Business Administration', 'Master of Science', 'Master of Computer Applications', 'Post Graduate Diploma'][getSeededRandom(5, i * 80)];
      
      const field = profileType === 'tech' || profileType === 'data'
        ? ['Computer Science', 'Information Technology', 'Electronics', 'Computer Engineering', 'Data Science'][getSeededRandom(5, i * 90)]
        : profileType === 'design'
          ? ['Design', 'Visual Communication', 'Digital Media', 'Animation', 'Fine Arts'][getSeededRandom(5, i * 100)]
          : ['Marketing', 'Business Administration', 'Mass Communication', 'Economics', 'Commerce'][getSeededRandom(5, i * 110)];
      
      const endYear = experiences[experiences.length - 1] 
        ? parseInt(experiences[experiences.length - 1].startDate.split('-')[0]) 
        : currentYear - getSeededRandom(5, i * 120);
      
      const startYear = endYear - (isUndergrad ? 4 : 2);
      
      education.push({
        degree,
        field,
        institution: university,
        location: ['Mumbai', 'Bangalore', 'Hyderabad', 'Delhi', 'Pune', 'Chennai'][getSeededRandom(6, i * 130)],
        startDate: `${startYear}-08`,
        endDate: `${endYear}-05`,
        current: false,
        gpa: (7.5 + getSeededRandom(25, i * 140) / 10).toFixed(1) + '/10'
    });
    }
    
    // Sort education by date (most recent first)
    education.sort((a, b) => b.endDate.localeCompare(a.endDate));
    
    // Generate preferences
    const jobTypes = [];
    if (getSeededRandom(2) === 0) jobTypes.push('full-time');
    if (getSeededRandom(3) === 0) jobTypes.push('remote');
    if (jobTypes.length === 0) jobTypes.push('full-time');
    
    const industries = [];
    switch (profileType) {
      case 'tech':
        industries.push('tech');
        if (getSeededRandom(2) === 0) industries.push('finance');
        break;
      case 'design':
        industries.push('media');
        if (getSeededRandom(2) === 0) industries.push('tech');
        break;
      case 'marketing':
        industries.push('media');
        if (getSeededRandom(2) === 0) industries.push('retail');
        break;
      case 'data':
        industries.push('tech');
        if (getSeededRandom(2) === 0) industries.push('finance');
        break;
    }
    
    // Generate basic info
    const names = ['Rahul Sharma', 'Priya Patel', 'Amit Kumar', 'Neha Singh', 'Vikram Mehta', 'Anjali Desai', 'Rajesh Verma', 'Pooja Gupta', 'Sanjay Joshi', 'Divya Malhotra'];
    const emails = ['rahul.sharma@gmail.com', 'priya.patel@gmail.com', 'amit.kumar@gmail.com', 'neha.singh@gmail.com', 'vikram.mehta@gmail.com', 'anjali.desai@gmail.com', 'rajesh.verma@gmail.com', 'pooja.gupta@gmail.com', 'sanjay.joshi@gmail.com', 'divya.malhotra@gmail.com'];
    const phones = ['9876543210', '8765432109', '7654321098', '6543210987', '5432109876', '4321098765', '3210987654', '2109876543', '1098765432', '0987654321'];
    
    const nameIndex = getSeededRandom(names.length, 150);
    
    return {
      skills,
      experience: experiences,
      education,
      preferences: {
        jobTypes,
        industries,
        locations: [experiences[0]?.location].filter(Boolean),
        jobTitle: experiences[0]?.title || '',
        workExperience: getExperienceLevel(experiences)
      },
      basicInfo: {
        name: names[nameIndex],
        email: emails[nameIndex],
        phone: phones[nameIndex],
        location: experiences[0]?.location || education[0]?.location || ''
      }
    };
  };
  
  // Generate job description based on profile type
  const generateJobDescription = (profileType, seed) => {
    const techDescriptions = [
      'Developed and maintained web applications using React and Node.js. Implemented responsive designs and optimized performance.',
      'Designed and implemented RESTful APIs. Collaborated with cross-functional teams to deliver high-quality software products.',
      'Led a team of developers to build scalable microservices. Improved system performance by 40% through code optimization.',
      'Developed frontend components using React and Redux. Implemented unit tests and integration tests using Jest and Enzyme.',
      'Built and maintained CI/CD pipelines using Jenkins and Docker. Deployed applications to AWS and monitored performance.'      
    ];
    
    const designDescriptions = [
      'Created user interfaces for web and mobile applications. Conducted user research and usability testing to improve UX.',
      'Designed wireframes, prototypes, and high-fidelity mockups. Collaborated with developers to ensure design implementation.',
      'Led the redesign of company website, resulting in 30% increase in user engagement. Created and maintained design system.',
      'Designed user interfaces for e-commerce platform. Conducted A/B testing to optimize conversion rates.',
      'Created visual designs for marketing campaigns. Developed brand guidelines and ensured consistent brand identity.'
    ];
    
    const marketingDescriptions = [
      'Managed digital marketing campaigns across multiple channels. Increased website traffic by 50% through SEO optimization.',
      'Developed and executed content marketing strategy. Created blog posts, social media content, and email newsletters.',
      'Led social media marketing efforts, growing followers by 200%. Analyzed campaign performance and optimized strategies.',
      'Managed Google Ads and Facebook Ads campaigns with a monthly budget of $50,000. Achieved 3x ROI on ad spend.',
      'Developed and implemented marketing automation workflows. Increased lead conversion rate by 25% through personalized email campaigns.'
    ];
    
    const dataDescriptions = [
      'Analyzed large datasets to identify trends and insights. Created dashboards and reports using Tableau and Power BI.',
      'Built machine learning models to predict customer behavior. Improved prediction accuracy by 20% through feature engineering.',
      'Developed ETL pipelines to process and transform data. Implemented data quality checks and monitoring systems.',
      'Conducted A/B tests to optimize user experience. Analyzed results and provided recommendations to product team.',
      'Created and maintained data warehouses. Designed data models and implemented data governance policies.'
    ];
    
    let descriptions;
    switch (profileType) {
      case 'tech': descriptions = techDescriptions; break;
      case 'design': descriptions = designDescriptions; break;
      case 'marketing': descriptions = marketingDescriptions; break;
      case 'data': descriptions = dataDescriptions; break;
      default: descriptions = techDescriptions;
    }
    
    return descriptions[seed % descriptions.length];
  };
  
  // Determine experience level based on work history
  const getExperienceLevel = (experiences) => {
    if (!experiences.length) return '';
    
    let totalMonths = 0;
    
    experiences.forEach(exp => {
      const startDate = new Date(exp.startDate);
      const endDate = exp.current ? new Date() : new Date(exp.endDate);
      
      if (isNaN(startDate) || isNaN(endDate)) return;
      
      const months = (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                     (endDate.getMonth() - startDate.getMonth());
      
      totalMonths += months > 0 ? months : 0;
    });
    
    const years = totalMonths / 12;
    
    if (years < 1) return 'fresher';
    if (years < 3) return 'junior';
    if (years < 5) return 'mid';
    if (years < 10) return 'senior';
    return 'expert';
  };

  // Handle continue button click
  const handleContinue = () => {
    if (file) {
      onNext();
    } else {
      setError('Please upload your resume or skip this step');
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold text-center mb-2">Upload Your Resume 📄</h1>
        <p className="text-gray-600 text-center mb-8">
          Let us extract your skills and experience automatically to save you time!
        </p>

        {/* Upload area */}
        <div 
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
            isDragging ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300 hover:border-yellow-400 hover:bg-gray-50'
          } ${error ? 'border-red-400' : ''}`}
          onClick={() => fileInputRef.current.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
          />
          
          {!file && !isUploading && (
            <div className="flex flex-col items-center">
              <div className="bg-gray-100 p-4 rounded-full mb-4">
                <Upload size={32} className="text-gray-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                Drag & drop your resume here
              </h3>
              <p className="text-gray-500 mb-4">
                or click to browse (PDF or Word, max 5MB)
              </p>
              <button
                type="button"
                className="px-6 py-2 bg-black text-white font-medium rounded-md hover:bg-gray-800 transition-colors"
              >
                Select File
              </button>
            </div>
          )}
          
          {isUploading && (
            <div className="flex flex-col items-center">
              <div className="w-full max-w-xs mb-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">Uploading...</span>
                  <span className="text-sm font-medium text-gray-700">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-yellow-400 h-2.5 rounded-full transition-all" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
              <p className="text-gray-500">{fileName}</p>
            </div>
          )}
          
          {isParsing && (
            <div className="flex flex-col items-center">
              <div className="flex items-center justify-center mb-4">
                <Loader size={32} className="text-yellow-400 animate-spin" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                Analyzing your resume...
              </h3>
              <p className="text-gray-500">
                We're extracting your skills and experience
              </p>
            </div>
          )}
          
          {file && !isUploading && !isParsing && (
            <div className="flex flex-col items-center">
              <div className="bg-green-100 p-4 rounded-full mb-4">
                <FileText size={32} className="text-green-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-1">
                {fileName}
              </h3>
              <p className="text-gray-500 mb-2">
                {formatFileSize(fileSize)}
              </p>
              
              {parseSuccess && (
                <div className="flex items-center text-green-600 mt-2">
                  <Check size={16} className="mr-1" />
                  <span className="text-sm">Successfully parsed your resume!</span>
                </div>
              )}
              
              <button
                type="button"
                className="mt-4 px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setFile(null);
                  setFileName('');
                  setFileSize(0);
                  setParseSuccess(false);
                  updateFormData('resume', { file: null, parsed: false });
                }}
              >
                Remove File
              </button>
            </div>
          )}
        </div>
        
        {/* Error message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md"
          >
            <div className="flex items-center">
              <AlertCircle size={16} className="text-red-500 mr-2" />
              <p className="text-red-700">{error}</p>
            </div>
          </motion.div>
        )}
        
        {/* Tips */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-100 rounded-md">
          <h3 className="text-md font-medium text-blue-800 mb-2 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Resume Tips
          </h3>
          <ul className="text-sm text-blue-700 space-y-1 ml-6 list-disc">
            <li>Use a clean, professional format</li>
            <li>Include keywords relevant to your target jobs</li>
            <li>Quantify your achievements when possible</li>
            <li>Proofread carefully for errors</li>
          </ul>
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between mt-8">
          <button
            type="button"
            className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors"
            onClick={onPrev}
          >
            Back
          </button>
          
          <div className="flex gap-4">
            <button
              type="button"
              className="px-6 py-2 text-gray-500 font-medium hover:text-gray-700 transition-colors"
              onClick={onSkip}
            >
              Skip for now
            </button>
            
            <motion.button
              type="button"
              className={`px-8 py-3 bg-black text-white font-bold rounded-md shadow-md hover:bg-gray-800 transition-all ${parseSuccess ? 'bg-green-600 hover:bg-green-700' : ''}`}
              onClick={handleContinue}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={isUploading || isParsing}
            >
              {parseSuccess ? 'Continue with Parsed Data' : 'Continue'}
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ResumeUploadStep;
